import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useCache } from '../hooks/useCache';

const TopRatedList = () => {
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('الكل');

  const fetcher = useMemo(() => () => api.getAdminHomePage(), []);
  const { data: cachedData, loading: cacheLoading, refetch } = useCache(
    'admin_homepage_toprated',
    fetcher,
    { ttl: 2 * 60 * 1000 } // 2 minutes cache
  );

  useEffect(() => {
    const fetchTopRated = async () => {
      try {
        setLoading(true);
        const data = cachedData || await api.getAdminHomePage();
        
        if (data && data.topRatedRestaurants) {
          const restaurants = data.topRatedRestaurants.map((r, index) => ({
            id: r.id,
            name: r.name,
            rating: Math.round(r.averageRating) || 0,
            reviews: '0',
            type: r.type,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(r.name)}&background=${index % 2 === 0 ? 'F27D72' : 'E96A3A'}&color=fff&size=128`
          }));
          
          // Filter based on selected filter
          let filtered = restaurants;
          if (filter === 'مطابخ') {
            filtered = restaurants.filter(r => r.type === 'restaurant');
          } else if (filter === 'متاجر') {
            filtered = restaurants.filter(r => r.type === 'store');
          }
          
          setTopRated(filtered);
        }
      } catch (error) {
        console.error('Error fetching top rated:', error);
        if (error.message && error.message.includes('لا يمكن الاتصال')) {
          console.warn('⚠️ الباك اند غير متاح. تأكد من تشغيله على http://localhost:3000');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTopRated();
  }, [filter, cachedData]);

  const StarIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '2px' }}>
      <path
        d="M6 0L7.38 4.31L12 4.75L8.5 7.94L9.64 12L6 9.54L2.36 12L3.5 7.94L0 4.75L4.62 4.31L6 0Z"
        fill="#FFAE3E"
      />
    </svg>
  );

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      minHeight: '600px',
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '32px',
      border: '1px solid rgba(233, 106, 58, 0.15)',
      boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      direction: 'rtl'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '28px',
        width: '100%',
        paddingBottom: '20px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ 
            width: '4px',
            height: '24px',
            backgroundColor: '#E96A3A',
            borderRadius: '2px'
          }}></div>
          <h2 style={{ 
            color: '#2B2F3F',
            fontSize: '20px',
            fontFamily: 'El Messiri, sans-serif',
            fontWeight: 700,
            textAlign: 'right',
            margin: 0
          }}>
            أعلى تقييم
          </h2>
        </div>

        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center'
        }}>
          {['الكل', 'مطابخ', 'متاجر'].map((filterOption, index) => (
            <button
              key={index}
              onClick={() => setFilter(filterOption)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: filter === filterOption ? 'none' : '1px solid rgba(102, 106, 122, 0.22)',
                backgroundColor: filter === filterOption ? '#E96A3A' : 'rgba(176, 179, 187, 0.13)',
                color: filter === filterOption ? '#FFFFFF' : '#666A7A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                fontWeight: filter === filterOption ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (filter !== filterOption) {
                  e.currentTarget.style.backgroundColor = 'rgba(176, 179, 187, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== filterOption) {
                  e.currentTarget.style.backgroundColor = 'rgba(176, 179, 187, 0.13)';
                }
              }}
            >
              {filterOption}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '20px',
        flex: 1,
        overflowY: 'auto'
      }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px',
            color: '#666A7A',
            fontFamily: 'Cairo, sans-serif'
          }}>
            جاري التحميل...
          </div>
        ) : topRated.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px',
            color: '#666A7A',
            fontFamily: 'Cairo, sans-serif'
          }}>
            لا توجد بيانات
          </div>
        ) : (
          topRated.map((item, index) => (
          <div
            key={item.id}
            style={{ 
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              padding: '16px',
              borderRadius: '12px',
              backgroundColor: index === 0 ? 'rgba(233, 106, 58, 0.04)' : 'transparent',
              border: index === 0 ? '1px solid rgba(233, 106, 58, 0.15)' : '1px solid transparent',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
              e.currentTarget.style.transform = 'translateX(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = index === 0 ? 'rgba(233, 106, 58, 0.04)' : 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            {/* Rank Number */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: index === 0 ? '#E96A3A' : index === 1 ? '#3FB984' : index === 2 ? '#F27D72' : 'rgba(176, 179, 187, 0.13)',
              color: index < 3 ? '#FFFFFF' : '#666A7A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              flexShrink: 0
            }}>
              {index + 1}
            </div>

            {/* Avatar */}
            <div style={{ 
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              border: '2px solid rgba(233, 106, 58, 0.15)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: '#F8F9FA'
            }}>
              <img 
                src={item.avatar}
                alt={item.name}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }}
              />
            </div>

            {/* Info */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              gap: '8px',
              alignItems: 'flex-end'
            }}>
              <h3 style={{ 
                color: '#2B2F3F',
                fontSize: '16px',
                fontFamily: 'Cairo, sans-serif',
                fontWeight: 600,
                lineHeight: '22px',
                margin: 0,
                textAlign: 'right'
              }}>
                {item.name}
              </h3>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%'
              }}>
                <span style={{ 
                  color: '#9CA3AF',
                  fontSize: '13px',
                  fontFamily: 'Cairo, sans-serif',
                  fontWeight: 500,
                  lineHeight: '20px'
                }}>
                  {item.reviews} تقييم
                </span>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '2px',
                  alignItems: 'center'
                }}>
                  {[...Array(item.rating)].map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TopRatedList;
